import {useState, useSyncExternalStore} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useIntl} from 'react-intl';
import {getActiveMatrixCallIdentity, getMatrixCallSessionSnapshot, subscribeMatrixCallSession} from '../../../services/matrixCallService';
import {matrixService} from '../../../services/matrixService';
import {fullName, type TeamMember} from '../../../pages/Team/teamFilters';
import {CallBubble} from '../components/CallBubble';
import {CallSurfaceBoundary} from '../components/CallSurfaceBoundary';
import {HeldCallRow, type HeldCall} from '../components/HeldCallRow';
import type {MatrixCallLeg} from '../model/types';
import {selectCallSurfaceState, setExpanded, setPanel} from '../store/callSurfaceSlice';
import {DevicesDialog} from '../group/GroupDialogs';
import {CallDialog} from '../components/CallDialog';
import {CallKeypad} from '../components/CallKeypad';
import {OutputPicker} from '../components/OutputPicker';
import {MatrixActiveBubble, MatrixCallingBubble, MatrixIncomingBubble, MatrixKeypadBubble, MatrixOutputBubble} from './MatrixBubbleViews';
import {FeedVideo} from './MatrixMedia';
import {MatrixStage} from './MatrixStage';
import {matrixActions} from './matrixActions';
import {hasLiveVideo, useMatrixCallMedia} from './useMatrixCallMedia';
import {useMatrixControls} from './useMatrixControls';
import {EscalationPicker} from './EscalationPicker';
import {callEscalation} from './callEscalationService';
import {matrixSurfaceMessages as m} from './matrixMessages';
import {VideoReliefToasts} from '../videoRelief/VideoReliefToasts';
import {ReliefScope} from '../videoRelief/videoReliefStore';
import {useMatrixVideoRelief} from '../videoRelief/useVideoRelief';

export const MatrixCallSurface = ({leg, held}: {leg: MatrixCallLeg; held?: HeldCall}) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const {call} = useSyncExternalStore(subscribeMatrixCallSession, getMatrixCallSessionSnapshot);
    const media = useMatrixCallMedia(call);
    const {expanded, panel} = useSelector(selectCallSurfaceState);
    const [devicesOpen, setDevicesOpen] = useState(false);
    const [escalating, setEscalating] = useState(false);
    const [escalationFailed, setEscalationFailed] = useState<string>();
    const name = leg.identity.name ?? '';
    useMatrixVideoRelief(leg.id, call, leg.capabilities.has('camera'), leg.phase === 'active' && leg.network === 'ok' && !leg.held && !leg.heldByPeer);
    const video = leg.kind === 'video' || hasLiveVideo(media.feeds.localUsermedia) || hasLiveVideo(media.feeds.remoteUsermedia);

    const controls = useMatrixControls(leg, media, {
        iconSize: expanded ? 24 : 20,
        onKeypad: () => dispatch(setPanel('keypad')),
        onSpeaker: () => dispatch(setPanel('output')),
        onDevices: () => setDevicesOpen(true),
        onAddCall: () => {
            setEscalationFailed(undefined);
            dispatch(setPanel('add'));
        },
    });

    const addPerson = (member: TeamMember) => {
        const personName = fullName(member);
        setEscalating(true);
        setEscalationFailed(undefined);
        void callEscalation.escalate({userId: Number(member.userId), name: personName}, video ? 'video' : 'audio', expanded).then(outcome => {
            setEscalating(false);
            if (outcome === 'not_started') {
                setEscalationFailed(personName);
            }
        });
    };

    const picker = (compact: boolean) => (
        <EscalationPicker
            peerId={getActiveMatrixCallIdentity()?.remoteUserId}
            peerName={name}
            localUserId={matrixService.getClient()?.getUserId() ?? null}
            busy={escalating}
            failedName={escalationFailed}
            onPick={addPerson}
            onBack={() => dispatch(setPanel('none'))}
            compact={compact}
            withHeader={compact}
        />
    );

    const backToCall = () => dispatch(setPanel('none'));
    const expand = () => dispatch(setExpanded(true));

    if (leg.phase === 'incoming') {
        return (
            <CallSurfaceBoundary resetKey={leg.id}>
                <CallBubble
                    tone="call"
                    urgent
                    label={intl.formatMessage(leg.kind === 'video' ? m.incomingVideoRegion : m.incomingRegion, {name})}
                    testId="one-to-one-ring-bubble"
                >
                    <MatrixIncomingBubble leg={leg}/>
                </CallBubble>
            </CallSurfaceBoundary>
        );
    }

    const preview = media.feeds.localUsermedia && hasLiveVideo(media.feeds.localUsermedia)
        ? <div className="h-[120px] overflow-hidden rounded-xl"><FeedVideo feed={media.feeds.localUsermedia} mirror/></div>
        : undefined;

    const bubble = leg.phase === 'calling'
        ? <MatrixCallingBubble leg={leg} onExpand={expand}/>
        : panel === 'keypad'
            ? <MatrixKeypadBubble onBack={backToCall}/>
            : panel === 'output'
                ? <MatrixOutputBubble onBack={backToCall}/>
                : panel === 'add' && leg.phase === 'active'
                    ? picker(true)
                    : <MatrixActiveBubble leg={leg} media={media} controls={controls} onExpand={expand}/>;

    return (
        <ReliefScope scope={leg.id}>
        <CallSurfaceBoundary resetKey={leg.id}>
            {expanded && leg.phase === 'active' ? (
                <MatrixStage leg={leg} media={media} controls={controls} onMinimize={() => dispatch(setExpanded(false))}/>
            ) : (
                <CallBubble tone="call" label={intl.formatMessage(m.callRegion, {name})} testId="one-to-one-bubble">
                    {held ? <div className="flex flex-col gap-3"><HeldCallRow {...held}/>{bubble}</div> : bubble}
                </CallBubble>
            )}
            {expanded && leg.phase === 'active' && held && (
                <div className="phq-call-ui fixed left-1/2 top-4 z-[56] -translate-x-1/2">
                    <HeldCallRow {...held} floating/>
                </div>
            )}
            {expanded && leg.phase === 'active' && panel === 'keypad' && (
                <CallDialog title={intl.formatMessage(m.keypadTitle)} closeLabel={intl.formatMessage(m.backToCall)} width="sm" onClose={backToCall}>
                    <CallKeypad onDigit={digit => void matrixActions.dtmf(digit)}/>
                </CallDialog>
            )}
            {expanded && leg.phase === 'active' && panel === 'output' && (
                <CallDialog title={intl.formatMessage(m.outputTitle)} closeLabel={intl.formatMessage(m.backToCall)} width="sm" onClose={backToCall}>
                    <OutputPicker onSelect={deviceId => matrixActions.switchDevice('audiooutput', deviceId)}/>
                </CallDialog>
            )}
            {expanded && leg.phase === 'active' && panel === 'add' && (
                <CallDialog title={intl.formatMessage(m.addTitle)} closeLabel={intl.formatMessage(m.backToCall)} width="sm" onClose={backToCall}>
                    {picker(false)}
                </CallDialog>
            )}
            {leg.phase === 'active' && <VideoReliefToasts/>}
            {devicesOpen && (
                <DevicesDialog
                    showCamera={leg.capabilities.has('camera')}
                    preview={preview}
                    onApply={matrixActions.switchDevice}
                    onClose={() => setDevicesOpen(false)}
                />
            )}
        </CallSurfaceBoundary>
        </ReliefScope>
    );
};
